// File: src/components/common/EmptyState.js
// -----------------------------------------------------
import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper 
} from '@mui/material';
import { 
  SearchOff as SearchOffIcon,
  Add as AddIcon,
  Inbox as InboxIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * Empty state component for when there's no content to display
 */
const EmptyState = ({ 
  title, 
  message, 
  actionText, 
  onAction, 
  icon = 'add',
  imgSrc 
}) => {
  const getIcon = () => {
    switch(icon) {
      case 'search':
        return <SearchOffIcon sx={{ fontSize: 80, color: 'primary.light', mb: 2 }} />;
      case 'add':
        return <AddIcon sx={{ fontSize: 80, color: 'primary.light', mb: 2 }} />;
      case 'inbox':
      default:
        return <InboxIcon sx={{ fontSize: 80, color: 'primary.light', mb: 2 }} />;
    }
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        py: 8, 
        px: 4, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        textAlign: 'center',
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px dashed',
        borderColor: 'divider'
      }}
    >
      {imgSrc ? (
        <Box 
          component="img" 
          src={imgSrc} 
          alt="Empty state illustration" 
          sx={{ 
            width: '100%', 
            maxWidth: 240, 
            height: 'auto', 
            mb: 3 
          }} 
        />
      ) : (
        getIcon()
      )}
      
      <Typography variant="h5" color="text.primary" gutterBottom>
        {title}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500 }}>
        {message}
      </Typography>
      
      {actionText && onAction && (
        <Button 
          variant="contained" 
          color="primary" 
          onClick={onAction}
          startIcon={icon === 'add' ? <AddIcon /> : null}
        >
          {actionText}
        </Button>
      )}
    </Paper>
  );
};

EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  icon: PropTypes.oneOf(['search', 'add', 'inbox']),
  imgSrc: PropTypes.string
};

export default EmptyState;